import apiClient from '@/lib/axios';
import type {
  ApiResponse,
  UserDto,
  UserLookupDto,
  RoleDto,
  RoleLookupDto,
  CreateUserFormData,
  UpdateUserFormData,
} from '../types';

// #region Users

/**
 * Get all users
 * @param isActive - Optional filter: true for active only, false for inactive only, undefined for all
 */
export const getUsers = async (isActive?: boolean): Promise<UserDto[]> => {
  const params = isActive !== undefined ? { isActive } : undefined;
  const response = await apiClient.get<ApiResponse<UserDto[]>>('/users', { params });
  return response.data.data;
};

/**
 * Get a single user by ID
 */
export const getUser = async (id: number): Promise<UserDto> => {
  const response = await apiClient.get<ApiResponse<UserDto>>(`/users/${id}`);
  return response.data.data;
};

/**
 * Get current authenticated user's profile
 */
export const getCurrentUser = async (): Promise<UserDto> => {
  const response = await apiClient.get<ApiResponse<UserDto>>('/users/me');
  return response.data.data;
};

/**
 * Create a new user (admin only)
 */
export const createUser = async (data: CreateUserFormData): Promise<UserDto> => {
  const response = await apiClient.post<ApiResponse<UserDto>>('/users', data);
  return response.data.data;
};

/**
 * Update an existing user
 */
export const updateUser = async (id: number, data: UpdateUserFormData): Promise<UserDto> => {
  const response = await apiClient.put<ApiResponse<UserDto>>(`/users/${id}`, data);
  return response.data.data;
};

/**
 * Delete (soft-delete) a user (admin only)
 */
export const deleteUser = async (id: number): Promise<void> => {
  await apiClient.delete(`/users/${id}`);
};

/**
 * Get active users for dropdown lookups
 */
export const getUserLookup = async (): Promise<UserLookupDto[]> => {
  const response = await apiClient.get<ApiResponse<UserLookupDto[]>>('/users/lookup');
  return response.data.data;
};

// #endregion

// #region Roles

/**
 * Get all roles
 * @param isActive - Optional filter: true for active only, false for inactive only, undefined for all
 */
export const getRoles = async (isActive?: boolean): Promise<RoleDto[]> => {
  const params = isActive !== undefined ? { isActive } : undefined;
  const response = await apiClient.get<ApiResponse<RoleDto[]>>('/roles', { params });
  return response.data.data;
};

/**
 * Get a single role by ID
 */
export const getRole = async (id: number): Promise<RoleDto> => {
  const response = await apiClient.get<ApiResponse<RoleDto>>(`/roles/${id}`);
  return response.data.data;
};

/**
 * Get active roles for dropdown lookups
 */
export const getRoleLookup = async (): Promise<RoleLookupDto[]> => {
  const response = await apiClient.get<ApiResponse<RoleLookupDto[]>>('/roles/lookup');
  return response.data.data;
};

// #endregion
